import { createCameraPairing, revokeCameraPairing } from "@/lib/api/recitals-api";
import type { VideoPairing, VideoSource } from "@/lib/live/video/video-types";

/**
 * La vidéo du récital : les règles que le récital pose sur le moteur vidéo.
 *
 * Le moteur sait publier, recevoir, appairer et couper. Il ne sait pas qu'un
 * récital montre deux caméras au plus, que seul l'interprète filme, ni ce que
 * les vignettes montrent : c'est ici. Voir « Le récital, premier client » dans
 * `docs/spec/video-engine.md`.
 */

/** Le genre de séance d'un récital, tel que l'API le nomme. */
export const RECITAL_SCOPE = "recital";

/**
 * Combien de caméras un récital montre à la fois, webcam comprise.
 *
 * Quoi qu'elles filment : des mains et un visage, deux angles sur le clavier ou
 * deux visages. Le récital cale toute image sur les notes, ce qu'une caméra
 * filme n'y change rien. Chaque caméra de plus coûte en revanche un flux à
 * chaque spectateur, qui le reçoit et le décode, et au serveur, qui le relaie
 * autant de fois qu'il y a de spectateurs.
 */
export const RECITAL_MAX_SOURCES = 2;

/** Appairer un appareil pour ce récital : l'interprète seulement, récital en cours. */
export function recitalPairing(recitalId: string): VideoPairing {
  return {
    scope: RECITAL_SCOPE,
    create: () => createCameraPairing(recitalId),
    revoke: (code) => revokeCameraPairing(recitalId, code),
  };
}

/**
 * Les caméras de ce poste, de la plus ancienne à la plus récente : sa webcam, et
 * les appareils appairés à son compte. Coupées à la source comprises : un
 * téléphone passé en arrière-plan reviendra filmer.
 */
export function performerSources(sources: readonly VideoSource[], owner: string | null): VideoSource[] {
  return sources.filter(
    (source) => source.local || (owner !== null && source.device === "paired" && source.owner === owner),
  );
}

/**
 * Les caméras à couper pour que ce poste n'en ait pas plus de deux : les plus
 * anciennes, jamais `keep` — celle qui vient d'arriver, ou la webcam qu'on vient
 * d'allumer.
 *
 * La règle se relit sur la liste du moment : après deux arrivées rapprochées,
 * les deux dernières restent, même si la coupure lancée à la première n'a pas
 * encore abouti.
 */
export function sourcesToCut(
  sources: readonly VideoSource[],
  owner: string | null,
  keep: string | null = null,
): VideoSource[] {
  const mine = performerSources(sources, owner);
  const kept = new Set<string>();

  if (keep !== null && mine.some((source) => source.id === keep)) {
    kept.add(keep);
  }

  for (let index = mine.length - 1; index >= 0 && kept.size < RECITAL_MAX_SOURCES; index -= 1) {
    kept.add(mine[index].id);
  }

  return mine.filter((source) => !kept.has(source.id));
}

/** Ce qu'il faut pour couper une caméra de ce poste. */
export interface RecitalCameraCuts {
  readonly sources: readonly VideoSource[];
  readonly self: string | null;
  stopWebcam(): Promise<void>;
  removeDevice(sourceId: string): Promise<boolean>;
}

/**
 * Coupe ce qui dépasse les deux caméras du récital : la webcam s'éteint, un
 * appareil sort de la salle. Sans attendre : la liste suivante dira ce qui est
 * parti.
 */
export function cutExtraSources(cuts: RecitalCameraCuts, keep: string | null): void {
  for (const source of sourcesToCut(cuts.sources, cuts.self, keep)) {
    if (source.local) {
      void cuts.stopWebcam();
    } else {
      void cuts.removeDevice(source.id);
    }
  }
}

/** Les appareils appairés de ce propriétaire : le récital qui s'arrête les coupe tous. */
export function pairedDevices(sources: readonly VideoSource[], owner: string | null): VideoSource[] {
  if (owner === null) {
    return [];
  }

  return sources.filter((source) => source.device === "paired" && source.owner === owner);
}

/**
 * Les caméras que les vignettes montrent, de la plus ancienne à la plus
 * récente : deux au plus, et aucune coupée à la source.
 *
 * Chez le spectateur, les dernières reçues : seul l'interprète publie. Quand une
 * troisième caméra entre, la plus ancienne met un instant à sortir de la salle ;
 * les deux dernières sont déjà à l'écran.
 *
 * Chez l'interprète, ses propres caméras : sa webcam, qu'il voit en miroir, et
 * ses appareils appairés.
 */
export function recitalSourcesToShow(
  sources: readonly VideoSource[],
  viewer: { listener: boolean; self: string | null },
): VideoSource[] {
  const candidates = viewer.listener
    ? sources.filter((source) => !source.local)
    : performerSources(sources, viewer.self);

  return candidates.filter((source) => !source.muted).slice(-RECITAL_MAX_SOURCES);
}
